import React, { useEffect, useState, useRef, memo } from 'react';
import { CheckCircle, XCircle, Map, Home, Plane } from 'lucide-react';
export function SalesLetter() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);
  return <article ref={sectionRef} className="max-w-4xl mx-auto px-4 py-12 text-lg text-slate-700 leading-relaxed bg-white" data-id="element-240">
      {/* Pregunta gancho */}
      <div className={`text-center mb-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-241">
        <p className="text-2xl md:text-3xl font-black text-slate-900 leading-tight" data-id="element-242">
          ¿Entrás al aula <span className="text-red-600" data-id="element-243">sin estar seguro</span>{' '}
          de cómo enseñar SIC con claridad?
        </p>
        <p className="mt-4 text-xl text-slate-600" data-id="element-244">
          El problema{' '}
          <span className="font-bold text-slate-900" data-id="element-245">no sos vos</span>. Es que{' '}
          <span className="font-bold text-red-600" data-id="element-246">
            nadie te dio una herramienta
          </span>{' '}
          pensada para tu realidad docente.
        </p>
      </div>

      {/* Dos columnas: realidad vs aula */}
      <div className={`grid md:grid-cols-2 gap-5 mb-14 transition-all duration-700 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-247">
        <div className="bg-green-50 rounded-2xl p-6 border-2 border-green-200" data-id="element-248">
          <h3 className="font-bold text-green-800 text-lg mb-4 flex items-center gap-2" data-id="element-249">
            <CheckCircle className="w-5 h-5 flex-shrink-0" data-id="element-250" />
            La contabilidad está en todos lados
          </h3>
          <ul className="space-y-3" data-id="element-251">
            {['En el precio del colectivo', 'En el sueldo que no alcanza', 'En las decisiones diarias'].map(item => <li key={item} className="flex items-start gap-2 text-slate-700" data-id="element-252">
                <span className="text-green-600 font-bold mt-0.5" data-id="element-253">✓</span>
                <span data-id="element-254">{item}</span>
              </li>)}
          </ul>
        </div>

        <div className="bg-red-50 rounded-2xl p-6 border-2 border-red-200" data-id="element-255">
          <h3 className="font-bold text-red-800 text-lg mb-4 flex items-center gap-2" data-id="element-256">
            <XCircle className="w-5 h-5 flex-shrink-0" data-id="element-257" />
            Pero en el aula pasa esto
          </h3>
          <ul className="space-y-3" data-id="element-258">
            {['Alumnos desconectados', 'Miradas perdidas', 'Celulares bajo el banco'].map(item => <li key={item} className="flex items-start gap-2" data-id="element-259">
                <span className="text-red-600 font-bold mt-0.5" data-id="element-260">✗</span>
                <span className="font-semibold text-red-700" data-id="element-261">{item}</span>
              </li>)}
          </ul>
          <div className="mt-5 pt-4 border-t border-red-200 text-center" data-id="element-262">
            <p className="font-bold text-red-700 italic" data-id="element-263">
              "Profe… ¿esto para qué sirve?"
            </p>
          </div>
        </div>
      </div>

      {/* The Solution: SIC 360 */}
      <div className="mb-12" data-id="element-264">
        <div className="text-center mb-10" data-id="element-265">
          <h2 className="text-5xl font-black text-slate-900 mb-4" data-id="element-266">
            Y ahí aparece <span className="text-orange-500" data-id="element-267">SIC 360</span>
          </h2>
        </div>

        <p className="mb-10 font-bold text-2xl text-slate-900" data-id="element-268">
          Es un{' '}
          <span className="text-orange-500" data-id="element-269">sistema de clases listas</span>,
          pensado específicamente para docentes de secundaria.
        </p>

        <div className="bg-white shadow-xl rounded-2xl p-10 border-2 border-slate-200 mb-10" data-id="element-270">
          <h4 className="font-bold text-xl mb-8 text-center text-slate-900" data-id="element-271">
            Un enfoque circular y progresivo que{' '}
            <span className="text-orange-500" data-id="element-272">cambia la lógica:</span>
          </h4>
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 text-center mb-8" data-id="element-273">
            <div className="flex-1" data-id="element-274">
              <p className="text-sm text-slate-500 uppercase tracking-wide mb-2" data-id="element-275">
                Del
              </p>
              <p className="font-bold text-blue-600 text-2xl" data-id="element-276">PARA QUÉ</p>
            </div>
            <div className="text-slate-300 text-2xl" data-id="element-277">→</div>
            <div className="flex-1" data-id="element-278">
              <p className="text-sm text-slate-500 uppercase tracking-wide mb-2" data-id="element-279">
                Al
              </p>
              <p className="font-bold text-blue-600 text-2xl" data-id="element-280">CÓMO</p>
            </div>
            <div className="text-slate-300 text-2xl" data-id="element-281">→</div>
            <div className="flex-1" data-id="element-282">
              <p className="text-sm text-slate-500 uppercase tracking-wide mb-2" data-id="element-283">
                Al
              </p>
              <p className="font-bold text-blue-600 text-2xl" data-id="element-284">QUÉ</p>
            </div>
          </div>
          <div className="h-px bg-slate-200 my-8" data-id="element-285"></div>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-286">
            <div className="flex items-center gap-3" data-id="element-287">
              <CheckCircle className="text-green-500 w-6 h-6 flex-shrink-0" data-id="element-288" />
              <span className="text-lg" data-id="element-289">
                De lo <span className="font-bold" data-id="element-290">concreto</span> a lo
                conceptual
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-291">
              <CheckCircle className="text-green-500 w-6 h-6 flex-shrink-0" data-id="element-292" />
              <span className="text-lg" data-id="element-293">
                De la <span className="font-bold" data-id="element-294">lógica del sistema</span> a la
                técnica
              </span>
            </div>
          </div>
          <div className="mt-8 text-center font-bold text-blue-600 text-xl" data-id="element-295">
            No memorizar. 👉{' '}
            <span className="text-orange-500" data-id="element-296">
              Entender el sistema completo.
            </span>
          </div>
        </div>
      </div>

      {/* Analogies */}
      <div className="mb-12" data-id="element-297">
        <h3 className="text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-298">
          Pensalo así
        </h3>
        <div className="grid gap-6" data-id="element-299">
          <div className="flex items-start gap-6 p-6 bg-white border-2 border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow" data-id="element-300">
            <div className="bg-orange-100 p-4 rounded-xl" data-id="element-301">
              <Map className="w-8 h-8 text-orange-600" data-id="element-302" />
            </div>
            <div data-id="element-303">
              <h4 className="font-bold text-xl mb-2" data-id="element-304">Un GPS educativo</h4>
              <p className="text-slate-600 text-lg" data-id="element-305">
                Sabés el recorrido.{' '}
                <span className="font-bold text-orange-500" data-id="element-306">
                  No improvisás.
                </span>
              </p>
            </div>
          </div>
          <div className="flex items-start gap-6 p-6 bg-white border-2 border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow" data-id="element-307">
            <div className="bg-blue-100 p-4 rounded-xl" data-id="element-308">
              <Home className="w-8 h-8 text-blue-600" data-id="element-309" />
            </div>
            <div data-id="element-310">
              <h4 className="font-bold text-xl mb-2" data-id="element-311">Un plano de una casa</h4>
              <p className="text-slate-600 text-lg" data-id="element-312">
                Primero <span className="font-bold" data-id="element-313">cimientos</span>. Después
                estructura.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-6 p-6 bg-white border-2 border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow" data-id="element-314">
            <div className="bg-indigo-100 p-4 rounded-xl" data-id="element-315">
              <Plane className="w-8 h-8 text-indigo-600" data-id="element-316" />
            </div>
            <div data-id="element-317">
              <h4 className="font-bold text-xl mb-2" data-id="element-318">Un manual de piloto</h4>
              <p className="text-slate-600 text-lg" data-id="element-319">
                Podés volar igual… pero con esto{' '}
                <span className="font-bold text-orange-500" data-id="element-320">
                  todo es más seguro.
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Origin Story */}
      <div className="bg-slate-50 p-10 rounded-2xl border-2 border-slate-200" data-id="element-321">
        <h3 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-322">
          SIC 360 nació de una{' '}
          <span className="text-orange-500" data-id="element-323">frustración real</span>
        </h3>
        <p className="mb-4 text-slate-700" data-id="element-324">
          De ver docentes preparados, con vocación, agotados. De clases que no
          funcionaban. De programas imposibles de bajar a tierra. De alumnos
          desconectados.
        </p>
        <p className="mb-4 font-bold text-slate-900 text-xl" data-id="element-325">
          La pregunta fue simple: ¿Y si el problema no es el docente…{' '}
          <span className="text-orange-500" data-id="element-326">sino el material?</span>
        </p>
        <p className="text-slate-700 text-lg" data-id="element-327">
          Así nació SIC 360. No como contenido. 👉{' '}
          <span className="font-bold text-blue-600" data-id="element-328">
            Como solución pedagógica.
          </span>
        </p>
      </div>
    </article>;
}